// per docs/shape-uc-27-alert-triggers-and-state.md §3
// per docs/shape-uc-27-alert-delivery-legibility.md §5
// per docs/caiet-uc-spec.md §UC-ALRT-01 §UC-ALRT-04 §UC-ALRT-11
// The alert history. Until this page existed the sidebar badge counted 7 000
// firing events with no route in the product to a single one.
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudSelect, MudTable, MudTag } from '@egov-moldova/mud-react';
import { apiClient } from '../../app/api-client';
import {
  dateInputToIso,
  EmptyRegisterMessage,
  FilterBar,
  formatDateTime,
  isoToDateInput,
  PageHeader,
  RouterLink,
  type ActiveFilter,
  DraftDateInput,
} from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { AlertsSectionNav } from '../../components/AlertsSectionNav';
import { severitySemantic, type RuleDto } from './AlertRulesPage';
import { HISTORY_PERIODS, periodRange } from './history-period';
import { TableFooter, usePageSize } from '../../components/TableFooter/TableFooter';

interface EventDto {
  id: string;
  ruleId: string;
  ruleName: string;
  status: 'pending' | 'firing' | 'resolved' | 'silenced';
  severity: 'critical' | 'warning' | 'info';
  title: string;
  body: string;
  firedAtIso: string;
  notificationSendCount: number;
  annotations?: Record<string, unknown>;
}

// A Detalii cell reads blank when the rule's template asked the firing for
// something it could not answer. The ingest records which substituents those
// were, so the row says that rather than showing a hole.
export function unresolvedSubstituents(event: Pick<EventDto, 'annotations'>): string {
  const value = event.annotations?.['unresolved_substituents'];
  return typeof value === 'string' ? value : '';
}

const SEVERITIES = ['critical', 'warning', 'info'] as const;

// The wireframe's Eveniment column. A repeat notification on a still-firing
// event is not a second trigger, and reading them as one hides how often the
// same problem is being re-announced.
export function eventLabelKey(status: string, notificationSendCount: number): string {
  if (status === 'firing') {
    return notificationSendCount > 1 ? 'alerts.event.renotified' : 'alerts.event.firing';
  }
  return `alerts.event.${status}`;
}

// The presets and their resolution moved to `history-period.ts` when the
// aggregate fișa's Istoric grew the same filter: one vocabulary for every
// history surface, which is the owner's 2026-08-23 ruling on filter patterns.
// Re-exported here because this module was where callers and tests found it.
export { periodRange };

export function AlertTriggersPage(): JSX.Element {
  const { t } = useTranslate();
  const [params, setParams] = useSearchParams();

  const search = params.get('q') ?? '';
  const severity = params.get('severity') ?? '';
  const ruleId = params.get('ruleId') ?? '';
  const period = params.get('period') ?? '';
  const customFrom = params.get('from') ?? '';
  const customTo = params.get('to') ?? '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const range = periodRange(period, customFrom, customTo);

  function setFilter(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  }

  const [pageSize, setPageSize] = usePageSize('alert-triggers');

  const events = useQuery({
    queryKey: ['alert-events', search, severity, ruleId, range.from, range.to, page, pageSize],
    queryFn: () => {
      const query = new URLSearchParams({
        tab: 'all',
        limit: String(pageSize),
        offset: String((page - 1) * pageSize),
      });
      if (search) query.set('q', search);
      if (severity) query.set('severity', severity);
      if (ruleId) query.set('ruleId', ruleId);
      if (range.from) query.set('from', range.from);
      if (range.to) query.set('to', range.to);
      return apiClient
        .get<{ alerts: EventDto[]; total: number }>(`/alerts?${query.toString()}`)
        .then((r) => r.data);
    },
  });

  // Only to resolve the rule filter's label and to know which rows can link to
  // a rule file; the history itself never joins client-side.
  const rules = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () =>
      apiClient.get<{ rules: RuleDto[] }>('/admin/alert-rules').then((r) => r.data.rules),
  });

  const ruleOptions = useMemo(
    () => [
      { value: '', label: t('alerts.filter.all_rules') },
      ...(rules.data ?? [])
        .map((r) => ({ value: r.id, label: r.displayName }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ro')),
    ],
    [rules.data, t],
  );
  // Events store the rule's machine name; the administrator reads the display
  // name everywhere else in the section, so resolve it when the rule still exists.
  const ruleNames = useMemo(
    () => new Map((rules.data ?? []).map((r) => [r.id, r.displayName])),
    [rules.data],
  );

  const rows = events.data?.alerts ?? [];
  const total = events.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasFilter = Boolean(search || severity || ruleId || period);

  // The period is one criterion even when it is drawn as three controls: its
  // chip reads the resolved range so a custom interval is legible without
  // reopening the panel. The search term gets no chip — it clears from the
  // box's own × (BaraFiltre.tsx:35).
  const activeFilters: ActiveFilter[] = [
    ...(period
      ? [
          {
            key: 'period',
            label: t('alerts.filter.period'),
            value:
              period === 'custom'
                ? t('common.filter.range_value', {
                    from: isoToDateInput(customFrom),
                    to: isoToDateInput(customTo),
                  })
                : t(`alerts.filter.period.${period}`),
            onRemove: () => {
              const next = new URLSearchParams(params);
              next.delete('period');
              next.delete('from');
              next.delete('to');
              next.delete('page');
              setParams(next, { replace: true });
            },
          },
        ]
      : []),
    ...(severity
      ? [
          {
            key: 'severity',
            label: t('alerts.column.severity'),
            value: t(`alerts.severity.${severity}`),
            onRemove: () => setFilter('severity', ''),
          },
        ]
      : []),
    ...(ruleId
      ? [
          {
            key: 'ruleId',
            label: t('alerts.column.alert'),
            value: ruleNames.get(ruleId) ?? ruleId,
            onRemove: () => setFilter('ruleId', ''),
          },
        ]
      : []),
  ];

  // The wireframe's history table (AdminAlerte.tsx admin-alert-history-table):
  // when, alert, severity, event, details. The rule name, the severity tag and
  // the clamped details are slotted; the rest is row data.
  const columns = [
    { key: 'when', label: t('alerts.triggers.column.when'), width: '150px' },
    { key: 'rule', label: t('alerts.column.alert'), width: '220px' },
    { key: 'severity', label: t('alerts.column.severity'), width: '120px' },
    { key: 'event', label: t('alerts.triggers.column.event'), width: '150px' },
    { key: 'details', label: t('alerts.triggers.column.details') },
  ];
  const tableRows = useMemo(
    () =>
      rows.map((e) => ({
        id: e.id,
        event: t(eventLabelKey(e.status, e.notificationSendCount)),
      })),
    [rows, t],
  );

  // An empty register keeps its table. The wireframe leaves the head, the
  // frame and the footer standing and puts the message in one centred cell
  // spanning every column (AdminAlerte.tsx:3936); a box in
  // place of the register hides what the register would even hold. Both
  // QueryState branches render this same node, because mud-table draws its
  // `empty` slot on its own once `rows` is [].
  const register = (
    <>
      <div className="simsm-alerts__history">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={tableRows}
          rowStyle="zebra"
          aria-label={t('alerts.triggers.table.aria_label')}
        >
          <EmptyRegisterMessage>
            {hasFilter ? t('alerts.triggers.empty_filtered') : t('alerts.triggers.empty')}
          </EmptyRegisterMessage>
          {rows.flatMap((e, index) => [
            <span key={`when-${e.id}`} slot={`cell-when-${index}`}>
              {formatDateTime(e.firedAtIso)}
            </span>,
            <span
              key={`rule-${e.id}`}
              slot={`cell-rule-${index}`}
              className="simsm-alerts__cell-name"
            >
              {ruleNames.has(e.ruleId) ? (
                // The wireframe's admin-name-link: ink at rest, underlined on hover.
                <RouterLink
                  to={`/system-admin/alerts/${e.ruleId}`}
                  size="sm"
                  variant="strict"
                  underline="hover"
                >
                  {ruleNames.get(e.ruleId)}
                </RouterLink>
              ) : (
                e.ruleName
              )}
            </span>,
            <span key={`severity-${e.id}`} slot={`cell-severity-${index}`}>
              <MudTag
                variant="status"
                size="sm"
                semantic={severitySemantic(e.severity)}
                label={t(`alerts.severity.${e.severity}`)}
              />
            </span>,
            <span
              key={`details-${e.id}`}
              slot={`cell-details-${index}`}
              className="simsm-alerts__history-details"
            >
              <span className="simsm-alerts__clamp" title={e.body || e.title}>
                {e.body || e.title}
              </span>
              {unresolvedSubstituents(e) ? (
                <MudTag
                  variant="info"
                  semantic="accent"
                  size="sm"
                  label={t('alerts.triggers.incomplete_template')}
                  aria-label={t('alerts.triggers.incomplete_template_detail', {
                    paths: unresolvedSubstituents(e),
                  })}
                />
              ) : null}
            </span>,
          ])}
        </MudTable>
      </div>
      <TableFooter
        total={total}
        page={Math.min(page, totalPages)}
        pageSize={pageSize}
        onPage={(next) => setFilter('page', next > 1 ? String(next) : '')}
        onPageSize={setPageSize}
      />
    </>
  );

  return (
    <section className="simsm-page simsm-alerts">
      <PageHeader title={t('alerts.section_title')} lede={t('alerts.section_lede')} />

      <AlertsSectionNav />

      <FilterBar
        search={{
          value: search,
          onChange: (value) => setFilter('q', value),
          placeholder: t('alerts.triggers.search.placeholder'),
          ariaLabel: t('alerts.triggers.search.aria'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => setParams(new URLSearchParams(), { replace: true })}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('alerts.triggers.filter.aria'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('alerts.filter.period')}</span>
              <MudSelect
                size="medium"
                value={period}
                aria-label={t('alerts.filter.period')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setFilter('period', e.detail.value)
                }
              >
                <option value="">{t('alerts.filter.period.all')}</option>
                {HISTORY_PERIODS.map((value) => (
                  <option key={value} value={value}>
                    {t(`alerts.filter.period.${value}`)}
                  </option>
                ))}
              </MudSelect>
            </label>
            {period === 'custom' ? (
              <div className="simsm-filter-bar__field-pair">
                <label className="simsm-filter-bar__field">
                  <span className="simsm-filter-bar__field-label">{t('alerts.filter.from')}</span>
                  <DraftDateInput
                    placeholder={t('common.date.placeholder')}
                    size="md"
                    value={isoToDateInput(customFrom)}
                    aria-label={t('alerts.filter.from')}
                    onMudChange={(e: CustomEvent<{ value: string }>) =>
                      setFilter('from', dateInputToIso(e.detail.value))
                    }
                  />
                </label>
                <label className="simsm-filter-bar__field">
                  <span className="simsm-filter-bar__field-label">{t('alerts.filter.to')}</span>
                  <DraftDateInput
                    placeholder={t('common.date.placeholder')}
                    size="md"
                    value={isoToDateInput(customTo)}
                    aria-label={t('alerts.filter.to')}
                    onMudChange={(e: CustomEvent<{ value: string }>) =>
                      setFilter('to', dateInputToIso(e.detail.value))
                    }
                  />
                </label>
              </div>
            ) : null}
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('alerts.column.severity')}</span>
              <MudSelect
                size="medium"
                value={severity}
                aria-label={t('alerts.column.severity')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setFilter('severity', e.detail.value)
                }
              >
                <option value="">{t('alerts.filter.all')}</option>
                {SEVERITIES.map((value) => (
                  <option key={value} value={value}>
                    {t(`alerts.severity.${value}`)}
                  </option>
                ))}
              </MudSelect>
            </label>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('alerts.column.alert')}</span>
              <MudSelect
                size="medium"
                value={ruleId}
                aria-label={t('alerts.column.alert')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setFilter('ruleId', e.detail.value)
                }
              >
                {ruleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </MudSelect>
            </label>
          </>
        }
      />

      <QueryState query={events} isEmpty={rows.length === 0} empty={register}>
        {register}
      </QueryState>
    </section>
  );
}
